import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCircleFill, PiTrashBold } from 'react-icons/pi';

import useScanLookup from '#/hooks/scan/useScanLookup';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogBodyFillClassName,
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import ScanAmbiguousDialog from '#/modules/commonComponents/scan/ScanAmbiguousDialog';
import { toast } from '#/utils/toast';

const ScanCodeDialog = ({
  open,
  onOpenChange,
  labels,
  lookup,
  getItemId,
  renderScannedRow,
  renderAmbiguousRow,
  onConfirm,
  getErrorMessage,
  isWarningRow,
  warningTitle = '以下明細為非需求物料：',
}) => {
  const [scannedById, setScannedById] = useState({});
  const [ambiguousOpen, setAmbiguousOpen] = useState(false);
  const [ambiguousCandidates, setAmbiguousCandidates] = useState([]);
  const [ambiguousCode, setAmbiguousCode] = useState('');
  const [ambiguousSession, setAmbiguousSession] = useState(0);
  const [hoveredWarningId, setHoveredWarningId] = useState(null);
  const ambiguousResolveRef = useRef(null);
  const scannedRowRefs = useRef({});
  const scannedListScrollRef = useRef(null);
  const prevScannedCountRef = useRef(0);

  const scannedList = Object.values(scannedById);
  const warningRows = typeof isWarningRow === 'function'
    ? scannedList.filter((row) => isWarningRow(row))
    : [];

  useEffect(() => {
    const count = scannedList.length;
    if (count > prevScannedCountRef.current) {
      const el = scannedListScrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
    prevScannedCountRef.current = count;
  }, [scannedList.length]);

  const finishAmbiguous = useCallback((rowOrNull) => {
    if (!ambiguousResolveRef.current) {
      setAmbiguousOpen(false);
      return;
    }
    const resolve = ambiguousResolveRef.current;
    ambiguousResolveRef.current = null;
    setAmbiguousOpen(false);
    setAmbiguousCandidates([]);
    setAmbiguousCode('');
    resolve(rowOrNull);
  }, []);

  const resolveAmbiguous = useCallback((candidates, code) => {
    return new Promise((resolve) => {
      ambiguousResolveRef.current = resolve;
      setAmbiguousCandidates(Array.isArray(candidates) ? candidates : []);
      setAmbiguousCode(code || '');
      setAmbiguousSession((prev) => prev + 1);
      setAmbiguousOpen(true);
    });
  }, []);

  function handlePick(item) {
    const id = getItemId?.(item);
    if (!id) {
      return;
    }

    let alreadyScanned = false;
    setScannedById((prev) => {
      if (prev[id]) {
        alreadyScanned = true;
        return prev;
      }
      return {
        ...prev,
        [id]: item,
      };
    });

    if (alreadyScanned) {
      toast.error(labels.duplicate || '已在掃描清單中');
    }
  }

  const {
    scanCode,
    setScanCode,
    isLookingUp,
    inputRef,
    handleKeyDown,
  } = useScanLookup({
    lookup,
    onPick: handlePick,
    onEmpty: () => {
      toast.error(labels.notFound || '未找到對應資料');
    },
    onError: (error) => {
      toast.error(
        getErrorMessage?.(error)
        || error?.message
        || '查詢失敗',
      );
    },
    resolveAmbiguous,
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
  }, [inputRef, open]);

  function resetDialogState() {
    setScannedById({});
    setScanCode('');
    setHoveredWarningId(null);
    scannedRowRefs.current = {};
    prevScannedCountRef.current = 0;
    finishAmbiguous(null);
  }

  function handleDialogOpenChange(nextOpen) {
    if (!nextOpen) {
      resetDialogState();
    }
    onOpenChange(nextOpen);
  }

  function handleRemoveScanned(itemId) {
    setHoveredWarningId((prev) => (prev === itemId ? null : prev));
    setScannedById((prev) => {
      if (!(itemId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }

  function handleCancel() {
    handleDialogOpenChange(false);
  }

  function handleConfirm() {
    if (scannedList.length === 0) {
      return;
    }
    onConfirm?.(scannedList);
    handleDialogOpenChange(false);
  }

  const ambiguousDescription = typeof labels.ambiguousDescription === 'function'
    ? labels.ambiguousDescription(ambiguousCode)
    : labels.ambiguousDescription;

  return (
    <>
      <AppDialog
        open={open}
        onOpenChange={handleDialogOpenChange}
        title={labels.title}
        description={labels.description}
        headerVariant="primary"
        maxWidthClassName={dialogWideMaxWidthClassName}
        contentClassName={dialogContentCompactGapClassName}
        bodyClassName={dialogBodyFillClassName}
        closeOnOutsideClick={false}
        confirmDisabled={scannedList.length === 0 || isLookingUp}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        confirmText={labels.confirm || '確認加入'}
        cancelText={labels.cancel || '取消'}
      >
        <div className="pt-4 min-h-0 flex flex-1 flex-col gap-4">
          {warningRows.length > 0 ? (
            <div
              className={cn(
                'form-validation-summary p-2.5',
                'max-h-50',
                'text-red-500 bg-red-50',
                'border border-red-200 rounded-md',
                'shrink-0 overflow-y-auto',
              )}
              role="alert"
            >
              <p className="mb-2 font-bold">{warningTitle}</p>
              <div className="flex flex-wrap gap-2 text-sm">
                {warningRows.map((row, index) => {
                  const itemId = getItemId(row);
                  const label = row?.stockNo || itemId || `item-${index}`;
                  // 勿用裸 itemId：id 為 0／'0' 時 `itemId || fallback` 仍為 '0'，多筆會撞 React key
                  const warningKey = `${itemId || 'warning'}-${index}`;
                  return (
                    <button
                      key={warningKey}
                      type="button"
                      className={cn(
                        'px-1.5 py-0.5',
                        'text-red-600',
                        'font-mono text-sm underline',
                        'border-0 bg-transparent rounded-sm',
                        'inline-flex items-center gap-1',
                        'cursor-pointer',
                        'hover:text-red-800 hover:bg-red-100',
                      )}
                      onMouseEnter={() => setHoveredWarningId(itemId || null)}
                      onMouseLeave={() => setHoveredWarningId(null)}
                      onClick={() => {
                        if (!itemId) {
                          return;
                        }
                        scannedRowRefs.current[itemId]?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'nearest',
                        });
                      }}
                    >
                      <PiCircleFill size={8} aria-hidden />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <FloatingInput
            id="scan-code-dialog-input"
            type="text"
            label={labels.inputLabel}
            tip={labels.inputTip}
            value={scanCode}
            readOnly={isLookingUp}
            register={() => ({
              ref: inputRef,
              value: scanCode,
              onChange: (event) => setScanCode(event.target.value),
            })}
            onKeyDown={handleKeyDown}
            containerClassName="w-full shrink-0"
            autoComplete="off"
            suppressInlineError
          />

          <div
            className={cn(
              'min-h-0 flex flex-1 flex-col overflow-hidden',
              'border border-gray-200 rounded-md',
            )}
          >
            <div
              className={cn(
                'px-4 py-2.5',
                'text-sm text-gray-600 bg-gray-100',
                'shrink-0 flex justify-between items-center gap-2',
              )}
            >
              <p className="font-medium">{labels.scannedListTitle || '已掃描'}</p>
              <p className="text-xs text-gray-500">
                {scannedList.length}
                {' '}
                筆
              </p>
            </div>
            <div
              ref={scannedListScrollRef}
              className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto"
            >
              {scannedList.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {labels.emptyList || '尚未掃描'}
                </p>
              ) : (
                scannedList.map((row, index) => {
                  const itemId = getItemId(row);
                  const rowKey = itemId ? `${itemId}-${index}` : `scanned-${index}`;
                  const isWarning = Boolean(isWarningRow?.(row));
                  const isHovered = Boolean(itemId) && hoveredWarningId === itemId;
                  return (
                    <div
                      key={rowKey}
                      ref={(node) => {
                        if (!itemId) {
                          return;
                        }
                        if (node) {
                          scannedRowRefs.current[itemId] = node;
                        } else {
                          delete scannedRowRefs.current[itemId];
                        }
                      }}
                      className={cn(
                        'px-4 py-2.5',
                        'flex items-center justify-between gap-2',
                        isWarning && 'bg-red-50',
                        isHovered && 'bg-red-200',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        {renderScannedRow?.(row)}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={cn(
                          'size-8 shrink-0',
                          'text-red-500',
                          'hover:bg-red-50 hover:text-red-600',
                        )}
                        aria-label="移除掃描項目"
                        onClick={() => handleRemoveScanned(itemId)}
                      >
                        <PiTrashBold className="size-4" />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </AppDialog>

      <ScanAmbiguousDialog
        key={ambiguousSession}
        open={ambiguousOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            finishAmbiguous(null);
            return;
          }
          setAmbiguousOpen(true);
        }}
        candidates={ambiguousCandidates}
        scannedCode={ambiguousCode}
        getItemId={getItemId}
        renderAmbiguousRow={renderAmbiguousRow}
        title={labels.ambiguousTitle || '請選擇'}
        description={ambiguousDescription}
        confirmText={labels.ambiguousConfirm || '加入'}
        onConfirm={(row) => finishAmbiguous(row)}
        onCancel={() => finishAmbiguous(null)}
      />
    </>
  );
};

ScanCodeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  labels: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.node,
    inputLabel: PropTypes.string.isRequired,
    inputTip: PropTypes.string,
    confirm: PropTypes.string,
    cancel: PropTypes.string,
    scannedListTitle: PropTypes.string,
    emptyList: PropTypes.string,
    notFound: PropTypes.string,
    duplicate: PropTypes.string,
    ambiguousTitle: PropTypes.string,
    ambiguousDescription: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func,
    ]),
    ambiguousConfirm: PropTypes.string,
  }).isRequired,
  lookup: PropTypes.func.isRequired,
  getItemId: PropTypes.func.isRequired,
  renderScannedRow: PropTypes.func.isRequired,
  renderAmbiguousRow: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  getErrorMessage: PropTypes.func,
  isWarningRow: PropTypes.func,
  warningTitle: PropTypes.string,
};

export default ScanCodeDialog;
